import React from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import {
  ActivityTrackerEventType,
  moduleOriginParamName,
  NavigationComponentTypes,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { isNotNull } from '@/modules/cms/helpers/isNotNull';
import { TrackingType } from '@/modules/home/types.ts';
import { type CategoryList as MeganavCategoryList } from '@/modules/meganav/types';
import { appendParams } from '@/modules/routing/appendParams/appendParams.ts';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  isOpen?: boolean;
} & MeganavCategoryList;

export function CategoryList({
  isOpen,
  title,
  itemsCollection: { items },
  footer,
}: Props) {
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const tabIndex = isOpen ? 0 : -1;

  function sendTracking(params: {
    href: string;
    label: string;
    componentType: string;
  }) {
    sendActivityTrackerEvent(ActivityTrackerEventType.NAVBAR_CLICK_ACTION, {
      ...params,
      schemaVersion: '1.0',
    });
  }

  function onLinkClick(href: string, label: string) {
    sendTracking({
      href,
      label,
      componentType: NavigationComponentTypes.TextLink,
    });
  }

  function onFooterLinkClick() {
    sendTracking({
      href: footer?.slug || '',
      label: footer?.label || '',
      componentType: NavigationComponentTypes.TextLinkFooter,
    });
  }

  return (
    <ul className={styles.list}>
      <li className={styles.baseListItem}>
        <Heading variant="sm">{title}</Heading>
      </li>
      {items.filter(isNotNull).map((item) => (
        <li className={styles.baseListItem} key={item.label}>
          <ExtendedLink
            href={appendParams(item.slug || '', [
              moduleOriginParamName,
              TrackingType.meganav,
            ])}
            tabIndex={tabIndex}
            onClick={() => onLinkClick(item.slug || '', item.label)}
            className={styles.anchor}
          >
            <Body
              variant="sm-regular"
              as="span"
              className={styles.anchor}
              style={
                item.colourOverride
                  ? ({
                      '--textColor': item.colourOverride,
                    } as React.CSSProperties)
                  : {}
              }
            >
              {item.label}
            </Body>
          </ExtendedLink>
        </li>
      ))}
      {footer && (
        <li className={styles.baseListItem}>
          <ExtendedLink
            className={styles['anchor--active']}
            href={footer.slug || ''}
            tabIndex={tabIndex}
            onClick={onFooterLinkClick}
          >
            {footer.label}
          </ExtendedLink>
        </li>
      )}
    </ul>
  );
}
